import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Course, Enrollment } from "@/lib/api-types";
import { cn, formatDuration, formatPrice } from "@/lib/utils";
import { BookOpen, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ProgressRing } from "./ProgressRing";

interface CourseCardProps {
	course: Course;
	enrollment?: Enrollment;
	className?: string;
}

export function CourseCard({ course, enrollment, className }: CourseCardProps) {
	const { t } = useTranslation();
	const isEnrolled = enrollment?.status === "active";
	const isDisabled = enrollment?.status === "disabled";

	const linkTo = isEnrolled ? `/learn/${course.id}` : `/courses/${course.id}`;

	return (
		<Link
			to={linkTo}
			className={cn(
				"group relative flex flex-col rounded-xl border border-white/10 bg-bg-elevated p-6 shadow-md",
				"transition-all duration-300 ease-out motion-reduce:transition-none",
				"hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-glow-sm",
				className,
			)}
		>
			<div className="mb-4 flex items-center justify-between">
				<Badge variant="outline" className="text-xs uppercase">
					{course.language}
				</Badge>
				{isDisabled ? (
					<Badge variant="outline">{t("admin.disabled")}</Badge>
				) : isEnrolled ? (
					<ProgressRing progress={enrollment.progress_percent} size="sm" />
				) : (
					<span className="text-sm font-semibold text-accent">
						{formatPrice(course.price_cents, course.currency, t("course.free"))}
					</span>
				)}
			</div>

			<h3 className="mb-2 text-lg font-semibold text-primary line-clamp-1">{course.title}</h3>
			<p className="mb-4 flex-1 text-sm text-secondary line-clamp-2">{course.description}</p>

			{isEnrolled && (
				<div className="mb-4">
					<div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-overlay">
						<div
							className="h-full rounded-full bg-accent transition-all duration-500 ease-out motion-reduce:transition-none"
							style={{ width: `${enrollment.progress_percent}%` }}
						/>
					</div>
					<p className="mt-1 text-xs text-muted">
						{enrollment.modules_completed}/{enrollment.modules_total}{" "}
						{t("course.modules", { count: enrollment.modules_total })}
					</p>
				</div>
			)}

			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3 text-xs text-muted">
					<span className="flex items-center gap-1">
						<BookOpen className="h-3.5 w-3.5" />
						{course.module_count}
					</span>
					<span className="flex items-center gap-1">
						<Clock className="h-3.5 w-3.5" />
						{formatDuration(course.total_duration_sec)}
					</span>
				</div>
				<Button size="sm" variant={isEnrolled ? "outline" : "default"} asChild>
					<span>
						{isDisabled
							? t("course.viewDetails")
							: isEnrolled
								? t("course.continue")
								: t("course.enroll")}
					</span>
				</Button>
			</div>
		</Link>
	);
}
